import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { HandCoins, Plus, Pencil, Trash2, Loader2, ArrowUpRight, ArrowDownLeft, CheckCircle2, Clock, AlertTriangle, Search, X, CalendarDays, DollarSign, History, ChevronUp, Tag, Filter, Link2, TrendingUp, TrendingDown, Wallet, ScrollText, Phone, Send, Copy, BellOff, Ban, Receipt } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { TruncatedText } from '../components/ui/truncated-text';
import DebtPaymentClaims from '../components/DebtPaymentClaims';
import { BorrowerPaymentDialog, BorrowerPaymentClaims } from '../components/BorrowerPayment';
import { AmountInput } from '../components/ui/amount-input';
import { copyText } from '../lib/clipboard';
import { wholeMoney } from '../components/charts/finops-charts';
import { Trans, useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Status tabs. "active" covers everything still to be repaid: untouched,
// partially paid and overdue (overdue is derived from due_date, not stored).
const DEBT_STATUS_TABS = [
  { value: 'active', labelKey: 'tabs.active', statuses: ['active', 'partial', 'overdue'] },
  { value: 'pending', labelKey: 'tabs.pending', statuses: ['pending_approval'] },
  { value: 'rejected', labelKey: 'tabs.rejected', statuses: ['rejected'] },
  { value: 'settled', labelKey: 'tabs.settled', statuses: ['settled'] },
  { value: 'all', labelKey: 'tabs.all', statuses: null },
];

// The message ends with the link, so inviteMessage(t, '').trim() is the text without it
const inviteMessage = (t, link) => t('invite.message', { link });

function Debts() {
  const { t } = useTranslation('debts');
  const [debts, setDebts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterDirection, setFilterDirection] = useState('all');
  const [statusTab, setStatusTab] = useState('active');
  const [filterCurrency, setFilterCurrency] = useState('all');
  const [filterYear, setFilterYear] = useState('all');
  const [filterTag, setFilterTag] = useState('');
  const [filterCounterparty, setFilterCounterparty] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingDebt, setEditingDebt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [userResults, setUserResults] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  // Bot username, for per-debt invite links (t.me/<bot>?start=q_<token>)
  const [botUsername, setBotUsername] = useState(null);

  // Payment dialog state
  const [paymentDebt, setPaymentDebt] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentNote, setPaymentNote] = useState('');
  const [payingDebt, setPayingDebt] = useState(false);

  // Payment history state
  const [expandedDebtId, setExpandedDebtId] = useState(null);
  const [payments, setPayments] = useState({});
  const [loadingPayments, setLoadingPayments] = useState({});

  // Personal balance state + filters
  const [balance, setBalance] = useState(null);
  const [balanceYear, setBalanceYear] = useState('all');
  const [balanceMonth, setBalanceMonth] = useState('all');
  const [balanceLoading, setBalanceLoading] = useState(false);

  // Approval requests state
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [resolvingApproval, setResolvingApproval] = useState(null);
  // Rejection reason dialog
  const [rejectingApproval, setRejectingApproval] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  // Approval history per debt
  const [approvalHistories, setApprovalHistories] = useState({});
  const [loadingHistory, setLoadingHistory] = useState({});
  const [expandedHistoryId, setExpandedHistoryId] = useState(null);
  // Payments the counterparty reported from the Telegram bot
  const [expandedClaimsId, setExpandedClaimsId] = useState(null);
  // Borrower of a debt shared with another FinOps user: report a payment (card / cash, receipt)
  const [borrowerPayDebt, setBorrowerPayDebt] = useState(null);
  const [expandedMyClaimsId, setExpandedMyClaimsId] = useState(null);
  const [myClaimsRefresh, setMyClaimsRefresh] = useState(0);
  const closeBorrowerPay = useCallback(() => setBorrowerPayDebt(null), []);

  const emptyForm = {
    direction: 'given',
    counterparty_name: '',
    counterparty_user_id: null,
    counterparty_phone: '',
    amount: '',
    currency: 'USD',
    description: '',
    tags: [],
    given_date: new Date().toISOString().split('T')[0],
    due_date: '',
  };
  const [form, setForm] = useState(emptyForm);

  const fmtUzs = (v) => `${Math.round(v).toLocaleString('uz-UZ')} UZS`;
  const fmtUsd = (v) => `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // Text filters reach the server 300 ms after the last keystroke, not on every one
  const [debouncedTag, setDebouncedTag] = useState(filterTag);
  const [debouncedCounterparty, setDebouncedCounterparty] = useState(filterCounterparty);
  useEffect(() => {
    const timer = setTimeout(() => { setDebouncedTag(filterTag); setDebouncedCounterparty(filterCounterparty); }, 300);
    return () => clearTimeout(timer);
  }, [filterTag, filterCounterparty]);
  const fetchSeq = useRef(0);

  const fetchDebts = useCallback(async () => {
    const seq = ++fetchSeq.current;  // a slower, older response must not overwrite a newer one
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterDirection !== 'all') params.set('direction', filterDirection);
      if (filterCurrency !== 'all') params.set('currency', filterCurrency);
      if (filterYear !== 'all') params.set('year', filterYear);
      if (debouncedTag.trim()) params.set('tag', debouncedTag.trim());
      if (debouncedCounterparty.trim()) params.set('counterparty', debouncedCounterparty.trim());
      if (filterDateFrom) params.set('date_from', filterDateFrom);
      if (filterDateTo) params.set('date_to', filterDateTo);
      const [debtsRes, summaryRes, approvalsRes] = await Promise.all([
        axios.get(`${API_URL}/api/debts?${params}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/debts/summary`, { withCredentials: true }),
        axios.get(`${API_URL}/api/my/debt-approvals`, { withCredentials: true }),
      ]);
      if (seq !== fetchSeq.current) return;
      setDebts(Array.isArray(debtsRes.data) ? debtsRes.data : []);
      setSummary(summaryRes.data);
      setPendingApprovals(Array.isArray(approvalsRes.data) ? approvalsRes.data : []);
    } catch {
      if (seq === fetchSeq.current) toast.error(t('toast.loadFailed'));
    } finally {
      if (seq === fetchSeq.current) setLoading(false);
    }
  }, [filterDirection, filterCurrency, filterYear, debouncedTag, debouncedCounterparty, filterDateFrom, filterDateTo, t]);

  useEffect(() => { fetchDebts(); }, [fetchDebts]);

  useEffect(() => {
    axios.get(`${API_URL}/api/telegram/bot-info`, { withCredentials: true })
      .then(res => setBotUsername(res.data?.username || null))
      .catch(() => { /* bot not configured — invite actions stay hidden */ });
  }, []);

  // Each debt has its own invite link; it stops asking for the number once the
  // counterparty has linked it, and expires when the phone changes or the debt closes.
  const inviteLinkFor = (debt) =>
    botUsername && debt?.counterparty_invite_token
      ? `https://t.me/${botUsername}?start=q_${debt.counterparty_invite_token}`
      : null;

  const copyInviteLink = async (debt) => {
    const link = inviteLinkFor(debt);
    if (!link) return;
    if (await copyText(inviteMessage(t, link))) toast.success(t('toast.inviteCopied'));
    else toast.error(t('toast.copyFailed'));
  };

  const shareInviteOnTelegram = (debt) => {
    const link = inviteLinkFor(debt);
    if (!link) return;
    const url = `https://t.me/share/url?url=${encodeURIComponent(link)}` +
      `&text=${encodeURIComponent(inviteMessage(t, '').trim())}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const fetchBalance = useCallback(async (year, month) => {
    setBalanceLoading(true);
    try {
      const params = new URLSearchParams();
      if (year !== 'all') params.set('year', year);
      if (month !== 'all') params.set('month', month);
      const res = await axios.get(`${API_URL}/api/my/transactions?${params}`, { withCredentials: true });
      setBalance(res.data.balance);
    } catch { /* silent */ }
    finally { setBalanceLoading(false); }
  }, []);

  useEffect(() => { fetchBalance(balanceYear, balanceMonth); }, [fetchBalance, balanceYear, balanceMonth]);

  const handleResolveApproval = async (approvalId, action, reason = '') => {
    setResolvingApproval(approvalId);
    try {
      const body = action === 'reject' && reason ? { reason } : {};
      await axios.post(`${API_URL}/api/debt-approvals/${approvalId}/${action}`, body, { withCredentials: true });
      toast.success(action === 'approve' ? t('toast.approved') : t('toast.rejected'));
      setRejectingApproval(null);
      setRejectReason('');
      fetchDebts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('common:status.error'));
    } finally {
      setResolvingApproval(null);
    }
  };

  const handleResubmit = async (debtId) => {
    try {
      const res = await axios.post(`${API_URL}/api/debts/${debtId}/resubmit`, {}, { withCredentials: true });
      toast.success(res.data?.message || t('toast.resubmitted'));
      // Refresh an open history panel so the new round shows up
      setApprovalHistories(h => { const next = { ...h }; delete next[debtId]; return next; });
      if (expandedHistoryId === debtId) setExpandedHistoryId(null);
      fetchDebts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('common:status.error'));
    }
  };

  const toggleApprovalHistory = async (debtId) => {
    if (expandedHistoryId === debtId) {
      setExpandedHistoryId(null);
      return;
    }
    setExpandedHistoryId(debtId);
    if (approvalHistories[debtId]) return; // already loaded
    setLoadingHistory(prev => ({ ...prev, [debtId]: true }));
    try {
      const res = await axios.get(`${API_URL}/api/debts/${debtId}/approval-history`, { withCredentials: true });
      setApprovalHistories(prev => ({ ...prev, [debtId]: Array.isArray(res.data) ? res.data : [] }));
    } catch {
      setApprovalHistories(prev => ({ ...prev, [debtId]: [] }));
    } finally {
      setLoadingHistory(prev => ({ ...prev, [debtId]: false }));
    }
  };

  // User search for linking
  useEffect(() => {
    if (userSearch.length < 2) { setUserResults([]); return; }
    const timeout = setTimeout(async () => {
      setSearchingUsers(true);
      try {
        const res = await axios.get(`${API_URL}/api/users/search?q=${encodeURIComponent(userSearch)}`, { withCredentials: true });
        setUserResults(Array.isArray(res.data) ? res.data : []);
      } catch { setUserResults([]); }
      finally { setSearchingUsers(false); }
    }, 300);
    return () => clearTimeout(timeout);
  }, [userSearch]);

  const openCreate = () => {
    setEditingDebt(null);
    setForm(emptyForm);
    setUserSearch('');
    setUserResults([]);
    setShowForm(true);
  };

  const openEdit = (debt) => {
    setEditingDebt(debt);
    setForm({
      direction: debt.direction,
      counterparty_name: debt.counterparty_name,
      counterparty_user_id: debt.counterparty_user_id,
      counterparty_phone: debt.counterparty_phone || '',
      amount: String(debt.amount),
      currency: debt.currency,
      description: debt.description || '',
      tags: debt.tags || [],
      given_date: debt.given_date ? debt.given_date.split('T')[0] : '',
      due_date: debt.due_date ? debt.due_date.split('T')[0] : '',
    });
    setUserSearch('');
    setUserResults([]);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.counterparty_name.trim()) { toast.error(t('validation.nameRequired')); return; }
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) { toast.error(t('validation.amountPositive')); return; }
    if (!form.given_date) { toast.error(t('validation.givenDateRequired')); return; }

    setSaving(true);
    try {
      const payload = {
        direction: form.direction,
        counterparty_name: form.counterparty_name.trim(),
        counterparty_user_id: form.counterparty_user_id || null,
        // Linked FinOps users are notified via their profile; phone is for everyone else
        counterparty_phone: form.counterparty_user_id ? null : (form.counterparty_phone.trim() || null),
        amount,
        currency: form.currency,
        description: form.description?.trim() || null,
        tags: form.tags.length > 0 ? form.tags : null,
        given_date: new Date(form.given_date).toISOString(),
        due_date: form.due_date ? new Date(form.due_date).toISOString() : null,
      };

      const res = editingDebt
        ? await axios.patch(`${API_URL}/api/debts/${editingDebt.id}`, payload, { withCredentials: true })
        : await axios.post(`${API_URL}/api/debts`, payload, { withCredentials: true });
      const saved = res.data || {};
      const savedMsg = editingDebt ? t('toast.updated') : t('toast.created');
      const tgStatus = saved.counterparty_telegram_status;
      if (saved.counterparty_phone && saved.counterparty_telegram_linked) {
        toast.success(editingDebt ? t('toast.updatedNotified') : t('toast.createdNotified'));
      } else if (tgStatus === 'stopped' || tgStatus === 'blocked') {
        toast.warning(savedMsg, {
          description: tgStatus === 'blocked'
            ? t('toast.telegramBlocked')
            : t('toast.telegramStopped'),
        });
      } else if (saved.counterparty_phone && inviteLinkFor(saved)) {
        toast.success(savedMsg, {
          description: t('toast.telegramNotLinked'),
          action: { label: t('toast.copyLink'), onClick: () => copyInviteLink(saved) },
          duration: 10000,
        });
      } else {
        toast.success(savedMsg);
      }
      setShowForm(false);
      fetchDebts();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common:status.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (debt) => {
    const isMirrorActive = debt.mirror_debt_id && !['pending_approval', 'rejected'].includes(debt.status);
    const confirmMsg = isMirrorActive
      ? t('confirm.deleteMirror', { name: debt.counterparty_name })
      : t('confirm.delete', { name: debt.counterparty_name });
    if (!window.confirm(confirmMsg)) return;
    try {
      const res = await axios.delete(`${API_URL}/api/debts/${debt.id}`, { withCredentials: true });
      if (res.data?.pending) {
        toast.success(t('toast.deletionRequested'));
      } else {
        toast.success(t('toast.deleted'));
      }
      fetchDebts();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('toast.deleteFailed'));
    }
  };

  const handleSettle = async (debt) => {
    // Full settle: use payment endpoint with remaining amount
    try {
      await axios.post(`${API_URL}/api/debts/${debt.id}/payments`, {
        amount: debt.remaining_amount,
        payment_date: new Date().toISOString(),
        note: "To'liq yopildi", // stored note shared by both parties: kept in Uzbek, not the viewer's language
      }, { withCredentials: true });
      toast.success(t('toast.settled'));
      fetchDebts();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common:status.error'));
    }
  };

  const openPaymentDialog = (debt) => {
    setPaymentDebt(debt);
    setPaymentAmount(String(debt.remaining_amount));
    setPaymentDate(new Date().toISOString().split('T')[0]);
    setPaymentNote('');
  };

  const closePaymentDialog = () => {
    setPaymentDebt(null);
    setPaymentAmount('');
    setPaymentNote('');
  };

  const handlePayment = async () => {
    if (!paymentDebt) return;
    const amount = parseFloat(paymentAmount);
    if (!amount || amount <= 0) { toast.error(t('validation.amountPositive')); return; }
    if (amount > paymentDebt.remaining_amount + 0.01) {
      toast.error(t('validation.paymentTooLarge', { remaining: paymentDebt.remaining_amount.toFixed(2) }));
      return;
    }

    setPayingDebt(true);
    try {
      const res = await axios.post(`${API_URL}/api/debts/${paymentDebt.id}/payments`, {
        amount,
        payment_date: paymentDate ? new Date(paymentDate).toISOString() : new Date().toISOString(),
        note: paymentNote.trim() || null,
      }, { withCredentials: true });
      if (res.data?.pending) {
        toast.success(res.data.message || t('toast.paymentRequested'));
      } else {
        toast.success(amount >= paymentDebt.remaining_amount ? t('toast.settled') : t('toast.paymentAccepted'));
      }
      closePaymentDialog();
      fetchDebts();
      if (expandedDebtId === paymentDebt.id) {
        fetchPayments(paymentDebt.id);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t('toast.paymentFailed'));
    } finally {
      setPayingDebt(false);
    }
  };

  const fetchPayments = async (debtId) => {
    setLoadingPayments(prev => ({ ...prev, [debtId]: true }));
    try {
      const res = await axios.get(`${API_URL}/api/debts/${debtId}/payments`, { withCredentials: true });
      setPayments(prev => ({ ...prev, [debtId]: Array.isArray(res.data) ? res.data : [] }));
    } catch {
      toast.error(t('toast.paymentsLoadFailed'));
    } finally {
      setLoadingPayments(prev => ({ ...prev, [debtId]: false }));
    }
  };

  const togglePaymentHistory = (debtId) => {
    if (expandedDebtId === debtId) {
      setExpandedDebtId(null);
    } else {
      setExpandedDebtId(debtId);
      if (!payments[debtId]) {
        fetchPayments(debtId);
      }
    }
  };

  const handleDeletePayment = async (debtId, paymentId) => {
    if (!window.confirm(t('confirm.deletePayment'))) return;
    try {
      await axios.delete(`${API_URL}/api/debts/${debtId}/payments/${paymentId}`, { withCredentials: true });
      toast.success(t('toast.paymentDeleted'));
      fetchPayments(debtId);
      fetchDebts();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('toast.deleteFailed'));
    }
  };

  const openNewForCounterparty = (debt) => {
    setEditingDebt(null);
    setForm({
      ...emptyForm,
      direction: debt.direction,
      counterparty_name: debt.counterparty_name,
      counterparty_user_id: debt.counterparty_user_id || null,
      counterparty_phone: debt.counterparty_phone || '',
    });
    setUserSearch('');
    setUserResults([]);
    setShowForm(true);
  };

  const selectUser = (u) => {
    setForm(f => ({ ...f, counterparty_name: u.name, counterparty_user_id: u.user_id, counterparty_phone: '' }));
    setUserSearch('');
    setUserResults([]);
  };

  const clearLinkedUser = () => {
    setForm(f => ({ ...f, counterparty_user_id: null }));
  };

  const getStatusBadge = (status, dueDate, rejectionReason) => {
    if (status === 'pending_approval') return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
        <Clock className="h-3 w-3" /> {t('status.pendingApproval')}
      </span>
    );
    if (status === 'rejected') return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 cursor-help"
        title={rejectionReason ? t('status.rejectionReasonTitle', { reason: rejectionReason }) : t('status.rejected')}
      >
        <X className="h-3 w-3" /> {t('status.rejected')}
      </span>
    );
    const isOverdue = status === 'active' && dueDate && new Date(dueDate) < new Date();
    if (isOverdue || status === 'overdue') return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
        <AlertTriangle className="h-3 w-3" /> {t('status.overdue')}
      </span>
    );
    if (status === 'settled') return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
        <CheckCircle2 className="h-3 w-3" /> {t('status.settled')}
      </span>
    );
    if (status === 'partial') return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
        <DollarSign className="h-3 w-3" /> {t('status.partial')}
      </span>
    );
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
        <Clock className="h-3 w-3" /> {t('status.active')}
      </span>
    );
  };

  const getDaysLeft = (dueDate) => {
    if (!dueDate) return null;
    const now = new Date();
    const due = new Date(dueDate);
    const diff = Math.ceil((due - now) / (1000 * 60 * 60 * 24));
    if (diff < 0) return { kind: 'overdue', text: t('days.overdue', { count: Math.abs(diff) }) };
    if (diff === 0) return { kind: 'today', text: t('days.today') };
    return { kind: 'left', text: t('days.left', { count: diff }) };
  };

  // Collect all unique tags from loaded debts for tag filter suggestions
  const allTags = [...new Set(debts.flatMap(d => d.tags || []))].sort();

  // Generate year options
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const clearAllFilters = () => {
    setFilterDirection('all');
    setFilterCurrency('all');
    setFilterYear('all');
    setFilterTag('');
    setFilterCounterparty('');
    setFilterDateFrom('');
    setFilterDateTo('');
  };

  const hasActiveFilters = filterDirection !== 'all' || filterCurrency !== 'all' ||
    filterYear !== 'all' || filterTag.trim() || filterCounterparty.trim() || filterDateFrom || filterDateTo;

  const tabCounts = useMemo(() => {
    const counts = {};
    DEBT_STATUS_TABS.forEach(tab => {
      counts[tab.value] = tab.statuses ? debts.filter(d => tab.statuses.includes(d.status)).length : debts.length;
    });
    return counts;
  }, [debts]);

  const visibleDebts = useMemo(() => {
    const tab = DEBT_STATUS_TABS.find(x => x.value === statusTab) || DEBT_STATUS_TABS[0];
    const list = tab.statuses ? debts.filter(d => tab.statuses.includes(d.status)) : debts;
    if (tab.value !== 'active') return list;
    // Most urgent first: overdue, then by nearest due date; no due date last
    const now = Date.now();
    const due = d => (d.due_date ? new Date(d.due_date).getTime() : Infinity);
    return [...list].sort((a, b) => {
      const ao = due(a) < now, bo = due(b) < now;
      if (ao !== bo) return ao ? -1 : 1;
      return due(a) - due(b);
    });
  }, [debts, statusTab]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-slate-100">
            {t('title')}
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            {t('subtitle')}
          </p>
        </div>
        <Button onClick={openCreate} className="gap-2">
          <Plus className="h-4 w-4" /> {t('newDebt')}
        </Button>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/30">
                  <ArrowUpRight className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{t('summary.givenActive')}</p>
                  <p className="text-lg font-bold text-orange-600 dark:text-orange-400">
                    {summary.given_active_usd > 0 && fmtUsd(summary.given_active_usd)}
                    {summary.given_active_usd > 0 && summary.given_active_uzs > 0 && ' + '}
                    {summary.given_active_uzs > 0 && fmtUzs(summary.given_active_uzs)}
                    {summary.given_active_usd === 0 && summary.given_active_uzs === 0 && '$0'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                  <ArrowDownLeft className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{t('summary.receivedActive')}</p>
                  <p className="text-lg font-bold text-blue-600 dark:text-blue-400">
                    {summary.received_active_usd > 0 && fmtUsd(summary.received_active_usd)}
                    {summary.received_active_usd > 0 && summary.received_active_uzs > 0 && ' + '}
                    {summary.received_active_uzs > 0 && fmtUzs(summary.received_active_uzs)}
                    {summary.received_active_usd === 0 && summary.received_active_uzs === 0 && '$0'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                  <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{t('summary.settled')}</p>
                  <p className="text-lg font-bold text-green-600 dark:text-green-400">{summary.settled_count}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30">
                  <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{t('summary.overdue')}</p>
                  <p className="text-lg font-bold text-red-600 dark:text-red-400">{summary.overdue_count}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Personal balance card */}
      <Card className="border border-slate-200 dark:border-slate-700">
        <CardContent className="pt-4 pb-4">
          {/* Header row */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <Wallet className="h-4 w-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{t('balance.title')}</span>
              {balanceLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
            </div>
            {/* Filters */}
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={balanceYear}
                onChange={e => { setBalanceYear(e.target.value); setBalanceMonth('all'); }}
                className="h-7 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs px-2 text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="all">{t('balance.allYears')}</option>
                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
              <select
                value={balanceMonth}
                onChange={e => setBalanceMonth(e.target.value)}
                disabled={balanceYear === 'all'}
                className="h-7 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs px-2 text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-40"
              >
                <option value="all">{t('balance.allMonths')}</option>
                {t('common:months', { returnObjects: true }).map((m, i) => (
                  <option key={i+1} value={i+1}>{m}</option>
                ))}
              </select>
              {(balanceYear !== 'all' || balanceMonth !== 'all') && (
                <button
                  onClick={() => { setBalanceYear('all'); setBalanceMonth('all'); }}
                  className="text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 underline"
                >
                  {t('common:actions.reset')}
                </button>
              )}
            </div>
          </div>

          {/* Period label */}
          {(balanceYear !== 'all' || balanceMonth !== 'all') && (
            <p className="text-xs text-slate-400 dark:text-slate-500 mb-3">
              {balanceMonth !== 'all'
                ? t('balance.periodMonth', { month: t('common:months', { returnObjects: true })[Number(balanceMonth)-1], year: balanceYear !== 'all' ? balanceYear : '' })
                : t('balance.periodYear', { year: balanceYear })}
            </p>
          )}

          {!balance ? (
            <div className="text-xs text-slate-400 text-center py-2">{t('common:status.noData')}</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {['USD', 'UZS'].map(cur => {
                const b = balance[cur];
                const net = parseFloat(b?.net_balance || 0);
                const income = parseFloat(b?.total_income || 0);
                const expense = parseFloat(b?.total_expense || 0);
                const fmt = v => cur === 'USD'
                  ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v)
                  : new Intl.NumberFormat('uz-UZ', { maximumFractionDigits: 0 }).format(v) + ' UZS';
                const hasData = income > 0 || expense > 0;
                return (
                  <div key={cur} className="rounded-lg bg-slate-50 dark:bg-slate-800/50 p-3 space-y-2">
                    <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">{cur}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                        <TrendingUp className="h-3.5 w-3.5 text-green-500 shrink-0" />
                        <span>{t('balance.income')}</span>
                      </div>
                      <span className={`text-xs font-medium ${income > 0 ? 'text-green-600 dark:text-green-400' : 'text-slate-400'}`}>{fmt(income)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                        <TrendingDown className="h-3.5 w-3.5 text-red-500 shrink-0" />
                        <span>{t('balance.expense')}</span>
                      </div>
                      <span className={`text-xs font-medium ${expense > 0 ? 'text-red-600 dark:text-red-400' : 'text-slate-400'}`}>{fmt(expense)}</span>
                    </div>
                    <div className="border-t border-slate-200 dark:border-slate-700 pt-2 flex items-center justify-between">
                      <span className="text-xs font-semibold text-slate-600 dark:text-slate-300">{t('balance.net')}</span>
                      {!hasData ? (
                        <span className="text-xs text-slate-400">—</span>
                      ) : (
                        <span className={`text-sm font-bold ${wholeMoney(net) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {wholeMoney(net) >= 0 ? '+' : ''}{fmt(wholeMoney(net))}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pending approval requests */}
      {pendingApprovals.length > 0 && (
        <Card className="border-2 border-yellow-300 dark:border-yellow-700 bg-yellow-50 dark:bg-yellow-900/10">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-yellow-800 dark:text-yellow-300 text-base">
              <Clock className="h-5 w-5" />
              {t('approvals.title', { count: pendingApprovals.length })}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {pendingApprovals.map(ap => {
              const info = ap.debt_info || {};
              const fmtAmt = info.currency === 'UZS'
                ? `${Math.round(info.amount || 0).toLocaleString('uz-UZ')} UZS`
                : `$${Number(info.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
              const payload = ap.payload || {};
              const isCreation = ap.type === 'creation';
              const isPayment = ap.type === 'payment';
              const isDeletion = ap.type === 'deletion';
              const payAmt = payload.currency === 'UZS'
                ? `${Math.round(payload.amount || 0).toLocaleString('uz-UZ')} UZS`
                : `$${Number(payload.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
              const isResolving = resolvingApproval === ap.id;
              return (
                <div key={ap.id} className="flex items-start justify-between gap-4 p-3 rounded-lg bg-white dark:bg-slate-800 border border-yellow-200 dark:border-yellow-800">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-semibold text-sm text-slate-900 dark:text-slate-100">
                        {ap.requested_by_name}
                      </span>
                      {isCreation && (
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                          <Trans t={t} i18nKey={info.direction === 'given' ? 'approvals.creationGiven' : 'approvals.creationReceived'} values={{ amount: fmtAmt }} components={{ strong: <strong /> }} />
                        </span>
                      )}
                      {isPayment && (
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                          <Trans t={t} i18nKey="approvals.payment" values={{ amount: payAmt }} components={{ strong: <strong /> }} />
                        </span>
                      )}
                      {isDeletion && (
                        <span className="text-xs text-red-600 dark:text-red-400 font-medium">
                          {t('approvals.deletion', { amount: fmtAmt })}
                        </span>
                      )}
                    </div>
                    {payload.description && (
                      <p className="text-xs text-slate-500 dark:text-slate-400">{payload.description}</p>
                    )}
                    {payload.note && (
                      <p className="text-xs text-slate-500 dark:text-slate-400">{t('approvals.note', { note: payload.note })}</p>
                    )}
                    <p className="text-[10px] text-slate-400 mt-1">
                      {new Date(ap.created_at).toLocaleString('uz-UZ')}
                    </p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 border-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 gap-1"
                      disabled={isResolving}
                      onClick={() => { setRejectingApproval(ap.id); setRejectReason(''); }}
                    >
                      <X className="h-3 w-3" /> {t('approvals.reject')}
                    </Button>
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 text-white gap-1"
                      disabled={isResolving}
                      onClick={() => handleResolveApproval(ap.id, 'approve')}
                    >
                      {isResolving ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
                      {t('approvals.approve')}
                    </Button>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="space-y-3">
        <div className="flex flex-wrap gap-3 items-center">
          <Select value={filterDirection} onValueChange={setFilterDirection}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder={t('filters.direction')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('filters.all')}</SelectItem>
              <SelectItem value="given">{t('filters.given')}</SelectItem>
              <SelectItem value="received">{t('filters.received')}</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant={showFilters ? "default" : "outline"}
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="gap-1.5"
          >
            <Filter className="h-3.5 w-3.5" />
            {t('filters.advanced')}
            {hasActiveFilters && (
              <span className="ml-1 h-4 w-4 rounded-full bg-blue-500 text-white text-[10px] flex items-center justify-center">!</span>
            )}
          </Button>
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearAllFilters} className="text-red-500 hover:text-red-700 gap-1">
              <X className="h-3.5 w-3.5" /> {t('common:actions.reset')}
            </Button>
          )}
        </div>

        {/* Extended filters */}
        {showFilters && (
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">{t('common:fields.currency')}</Label>
                  <Select value={filterCurrency} onValueChange={setFilterCurrency}>
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('filters.all')}</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="UZS">UZS</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">{t('common:fields.year')}</Label>
                  <Select value={filterYear} onValueChange={setFilterYear}>
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('filters.all')}</SelectItem>
                      {yearOptions.map(y => (
                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">{t('filters.dateFrom')}</Label>
                  <Input type="date" className="h-9" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">{t('filters.dateTo')}</Label>
                  <Input type="date" className="h-9" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">{t('filters.counterparty')}</Label>
                  <Input className="h-9" placeholder={t('filters.counterpartyPlaceholder')} value={filterCounterparty} onChange={e => setFilterCounterparty(e.target.value)} />
                </div>
                <div className="space-y-1.5 col-span-2 md:col-span-1">
                  <Label className="text-xs">{t('filters.tag')}</Label>
                  <Input className="h-9" placeholder={t('filters.tagPlaceholder')} value={filterTag} onChange={e => setFilterTag(e.target.value)} list="tag-suggestions" />
                  {allTags.length > 0 && (
                    <datalist id="tag-suggestions">
                      {allTags.map(tag => <option key={tag} value={tag} />)}
                    </datalist>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create/Edit Form Dialog */}
      <Dialog open={showForm} onOpenChange={(open) => { if (!saving) setShowForm(open); }}>
        <DialogContent className="max-w-xl" data-testid="debt-form-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HandCoins className="h-5 w-5 text-blue-600" />
              {editingDebt ? t('form.editTitle') : t('form.createTitle')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Direction */}
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setForm(f => ({ ...f, direction: 'given' }))}
                className={`flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-all ${
                  form.direction === 'given'
                    ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300'
                    : 'border-slate-200 dark:border-slate-700 text-slate-500 hover:border-slate-300'
                }`}
              >
                <ArrowUpRight className="h-5 w-5" />
                <span className="font-medium">{t('form.iGave')}</span>
              </button>
              <button
                type="button"
                onClick={() => setForm(f => ({ ...f, direction: 'received' }))}
                className={`flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-all ${
                  form.direction === 'received'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                    : 'border-slate-200 dark:border-slate-700 text-slate-500 hover:border-slate-300'
                }`}
              >
                <ArrowDownLeft className="h-5 w-5" />
                <span className="font-medium">{t('form.iReceived')}</span>
              </button>
            </div>

            {/* Counterparty */}
            <div className="space-y-2">
              <Label>{form.direction === 'given' ? t('form.givenTo') : t('form.receivedFrom')}</Label>
              <Input
                placeholder={t('form.namePlaceholder')}
                value={form.counterparty_name}
                onChange={e => setForm(f => ({ ...f, counterparty_name: e.target.value, counterparty_user_id: null }))}
              />
              {form.counterparty_user_id && (
                <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                  <CheckCircle2 className="h-4 w-4" />
                  <span>{t('form.linkedUser')}</span>
                  <button type="button" onClick={clearLinkedUser} className="text-red-500 hover:text-red-700">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
              {!form.counterparty_user_id && (
                <div className="relative">
                  <div className="flex items-center gap-2">
                    <Search className="h-4 w-4 text-slate-400" />
                    <Input
                      placeholder={t('form.userSearchPlaceholder')}
                      value={userSearch}
                      onChange={e => setUserSearch(e.target.value)}
                      className="text-sm"
                    />
                    {searchingUsers && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
                  </div>
                  {userResults.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                      {userResults.map(u => (
                        <button
                          key={u.user_id}
                          type="button"
                          onClick={() => selectUser(u)}
                          className="w-full text-left px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 text-sm flex items-center justify-between"
                        >
                          <span className="font-medium">{u.name}</span>
                          <span className="text-xs text-slate-400">{u.email}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* External counterparty phone (Telegram notifications) */}
            {!form.counterparty_user_id && (
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5">
                  <Phone className="h-3.5 w-3.5" /> {t('form.phone')}
                </Label>
                <Input
                  type="tel"
                  inputMode="tel"
                  placeholder="+998 90 123 45 67"
                  value={form.counterparty_phone}
                  onChange={e => setForm(f => ({ ...f, counterparty_phone: e.target.value }))}
                />
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {t('form.phoneHint')}
                </p>
              </div>
            )}

            {/* Amount + Currency */}
            <div className="grid grid-cols-3 gap-3 items-end">
              <div className="col-span-2 space-y-2">
                <Label>{t('form.amount')}</Label>
                <AmountInput
                  placeholder="0.00"
                  value={form.amount}
                  onChange={(val) => setForm(f => ({ ...f, amount: val }))}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('common:fields.currency')}</Label>
                <Select value={form.currency} onValueChange={v => setForm(f => ({ ...f, currency: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="UZS">UZS</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{t('form.givenDate')}</Label>
                <Input
                  type="date"
                  value={form.given_date}
                  onChange={e => setForm(f => ({ ...f, given_date: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('form.dueDate')}</Label>
                <Input
                  type="date"
                  value={form.due_date}
                  onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))}
                />
              </div>
            </div>

            {/* Date period visual */}
            {form.given_date && form.due_date && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                <CalendarDays className="h-4 w-4 text-slate-500" />
                <span className="text-sm text-slate-600 dark:text-slate-400">
                  {new Date(form.given_date).toLocaleDateString('uz-UZ')} — {new Date(form.due_date).toLocaleDateString('uz-UZ')}
                  {' '}
                  ({t('form.periodDays', { count: Math.max(0, Math.ceil((new Date(form.due_date) - new Date(form.given_date)) / (1000 * 60 * 60 * 24))) })})
                </span>
              </div>
            )}

            {/* Description */}
            <div className="space-y-2">
              <Label>{t('form.description')}</Label>
              <Input
                placeholder={t('form.descriptionPlaceholder')}
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              />
            </div>

            {/* Tags */}
            <div className="space-y-2">
              <Label>{t('form.tags')}</Label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {form.tags.map((tag, idx) => (
                  <span key={idx} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400">
                    <Tag className="h-3 w-3" />
                    {tag}
                    <button type="button" onClick={() => setForm(f => ({ ...f, tags: f.tags.filter((_, i) => i !== idx) }))} className="hover:text-red-500">
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder={t('form.tagPlaceholder')}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const val = e.target.value.trim();
                      if (val && !form.tags.includes(val)) {
                        setForm(f => ({ ...f, tags: [...f.tags, val] }));
                        e.target.value = '';
                      }
                    }
                  }}
                  className="text-sm"
                />
              </div>
            </div>

          </div>
          <DialogFooter className="gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowForm(false)} disabled={saving}>{t('common:actions.cancel')}</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="debt-form-submit">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {editingDebt ? t('common:actions.save') : t('common:actions.add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status tabs */}
      <div className="tab-strip flex items-center gap-1 border-b border-slate-200 dark:border-slate-700 overflow-x-auto">
        {DEBT_STATUS_TABS.map(tab => (
          <button
            key={tab.value}
            onClick={() => setStatusTab(tab.value)}
            data-testid={`debt-status-tab-${tab.value}`}
            className={`relative px-4 py-2.5 text-sm font-medium transition-colors -mb-px border-b-2 whitespace-nowrap ${
              statusTab === tab.value
                ? 'border-emerald-600 text-emerald-700 dark:text-emerald-400'
                : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
            }`}
          >
            {t(tab.labelKey)}
            <span className={`ml-2 inline-flex items-center justify-center min-w-[1.25rem] px-1.5 py-0.5 rounded-full text-xs ${
              statusTab === tab.value
                ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300'
                : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
            }`}>
              {tabCounts[tab.value] || 0}
            </span>
          </button>
        ))}
      </div>

      {/* Debts List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      ) : debts.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <HandCoins className="h-12 w-12 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
            <p className="text-slate-500 dark:text-slate-400">{t('list.empty')}</p>
            <Button onClick={openCreate} variant="outline" className="mt-4 gap-2">
              <Plus className="h-4 w-4" /> {t('list.addFirst')}
            </Button>
          </CardContent>
        </Card>
      ) : visibleDebts.length === 0 ? (
        <div className="text-center py-12 text-slate-400 dark:text-slate-500" data-testid="debts-tab-empty">
          {t('list.emptyTab', { tab: t(DEBT_STATUS_TABS.find(x => x.value === statusTab)?.labelKey || 'tabs.all') })}
        </div>
      ) : (
        <div className="space-y-3">
          {visibleDebts.map(debt => {
            const isGiven = debt.direction === 'given';
            const daysLeft = debt.status === 'settled' ? null : getDaysLeft(debt.due_date);
            const paidPct = debt.amount > 0 ? Math.min(100, (debt.paid_amount / debt.amount) * 100) : 0;
            const isExpanded = expandedDebtId === debt.id;
            const debtPayments = payments[debt.id] || [];
            const isLoadingPayments = loadingPayments[debt.id];
            return (
              <Card key={debt.id} className="hover:shadow-md transition-shadow">
                <CardContent className="py-4">
                  <div className="flex items-start justify-between gap-4">
                    {/* Left: Icon + Info */}
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className={`p-2 rounded-lg shrink-0 ${
                        isGiven
                          ? 'bg-orange-100 dark:bg-orange-900/30'
                          : 'bg-blue-100 dark:bg-blue-900/30'
                      }`}>
                        {isGiven
                          ? <ArrowUpRight className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                          : <ArrowDownLeft className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        }
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-slate-900 dark:text-slate-100 truncate">
                            {debt.counterparty_name}
                          </span>
                          {debt.counterparty_user_id && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400">
                              FinOps
                            </span>
                          )}
                          {debt.mirror_debt_id && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400" title={t('list.mirrorTitle')}>
                              <Link2 className="h-2.5 w-2.5" /> {t('list.mirror')}
                            </span>
                          )}
                          {debt.counterparty_phone && (() => {
                            const status = debt.counterparty_telegram_status || (debt.counterparty_telegram_linked ? 'linked' : 'not_linked');
                            const badge = 'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium';
                            const isOpen = ['active', 'partial', 'overdue'].includes(debt.status);
                            if (status === 'linked') return (
                              <>
                                <span className={`${badge} bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400`}
                                  title={t('telegram.linkedTitle', { phone: debt.counterparty_phone })}>
                                  <Send className="h-2.5 w-2.5" /> Telegram
                                </span>
                                {debt.counterparty_response === 'confirmed' ? (
                                  <span className={`${badge} bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400`}
                                    title={debt.counterparty_responded_at
                                      ? t('telegram.confirmedTitleDate', { date: new Date(debt.counterparty_responded_at).toLocaleDateString('uz-UZ') })
                                      : t('telegram.confirmedTitle')}>
                                    <CheckCircle2 className="h-2.5 w-2.5" /> {t('telegram.confirmed')}
                                  </span>
                                ) : isOpen && (
                                  <span className={`${badge} bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300`}
                                    title={t('telegram.awaitingTitle')}>
                                    <Clock className="h-2.5 w-2.5" /> {t('telegram.awaiting')}
                                  </span>
                                )}
                              </>
                            );
                            if (status === 'stopped') return (
                              <span className={`${badge} bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300`}
                                title={t('telegram.stoppedTitle', { phone: debt.counterparty_phone })}>
                                <BellOff className="h-2.5 w-2.5" /> {t('telegram.stopped')}
                              </span>
                            );
                            if (status === 'blocked') return (
                              <span className={`${badge} bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400`}
                                title={t('telegram.blockedTitle', { phone: debt.counterparty_phone })}>
                                <Ban className="h-2.5 w-2.5" /> {t('telegram.blocked')}
                              </span>
                            );
                            // Rejected: the status badge ("rejected") and the reason banner already say it
                            if (status === 'rejected') return null;
                            return (
                              <span className={`${badge} bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400`}
                                title={t('telegram.notLinkedTitle', { phone: debt.counterparty_phone })}>
                                <Phone className="h-2.5 w-2.5" /> {t('telegram.notLinked')}
                                {inviteLinkFor(debt) && (
                                  <>
                                    <button type="button" onClick={() => copyInviteLink(debt)} className="ml-1 hover:text-amber-900 dark:hover:text-amber-200" title={t('telegram.copyInvite')}>
                                      <Copy className="h-2.5 w-2.5" />
                                    </button>
                                    <button type="button" onClick={() => shareInviteOnTelegram(debt)} className="hover:text-amber-900 dark:hover:text-amber-200" title={t('telegram.sendInvite')}>
                                      <Send className="h-2.5 w-2.5" />
                                    </button>
                                  </>
                                )}
                              </span>
                            );
                          })()}
                          {getStatusBadge(debt.status, debt.due_date, debt.rejection_reason)}
                          {debt.open_payment_claims > 0 && (
                            <button type="button" onClick={() => setExpandedClaimsId(debt.id)}
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
                              title={t('list.claimReportedTitle')}>
                              <Receipt className="h-2.5 w-2.5" /> {t('list.claimReported', { count: debt.open_payment_claims })}
                            </button>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 dark:text-slate-400">
                          <span>{isGiven ? t('list.givenOn', { date: new Date(debt.given_date).toLocaleDateString('uz-UZ') }) : t('list.receivedOn', { date: new Date(debt.given_date).toLocaleDateString('uz-UZ') })}</span>
                          {debt.due_date && (
                            <>
                              <span>•</span>
                              <span>{t('list.due', { date: new Date(debt.due_date).toLocaleDateString('uz-UZ') })}</span>
                            </>
                          )}
                          {daysLeft && (
                            <>
                              <span>•</span>
                              <span className={
                                daysLeft.kind === 'overdue' ? 'text-red-500 font-medium' :
                                daysLeft.kind === 'today' ? 'text-amber-500 font-medium' :
                                'text-slate-500'
                              }>
                                {daysLeft.text}
                              </span>
                            </>
                          )}
                        </div>
                        {debt.description && (
                          <TruncatedText text={debt.description} maxLength={60} className="text-sm text-slate-500 dark:text-slate-400 mt-1 truncate block" />
                        )}
                        {debt.tags && debt.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {debt.tags.map((tag, idx) => (
                              <span key={idx} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400">
                                <Tag className="h-2.5 w-2.5" />{tag}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Progress bar for partial payments */}
                        {debt.paid_amount > 0 && (
                          <div className="mt-2">
                            <div className="flex items-center justify-between text-xs mb-1">
                              <span className="text-slate-500 dark:text-slate-400">
                                {t('list.paid', { amount: debt.currency === 'USD' ? fmtUsd(debt.paid_amount) : fmtUzs(debt.paid_amount) })}
                              </span>
                              <span className="text-slate-500 dark:text-slate-400">
                                {t('list.remaining', { amount: debt.currency === 'USD' ? fmtUsd(debt.remaining_amount) : fmtUzs(debt.remaining_amount) })}
                              </span>
                            </div>
                            <div className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all ${
                                  paidPct >= 100
                                    ? 'bg-green-500'
                                    : paidPct >= 50
                                    ? 'bg-amber-500'
                                    : 'bg-blue-500'
                                }`}
                                style={{ width: `${paidPct}%` }}
                              />
                            </div>
                            <p className="text-[10px] text-slate-400 mt-0.5 text-right">{paidPct.toFixed(0)}%</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Right: Amount + Actions */}
                    <div className="text-right shrink-0">
                      <p className={`text-lg font-bold ${
                        isGiven ? 'text-orange-600 dark:text-orange-400' : 'text-blue-600 dark:text-blue-400'
                      }`}>
                        {debt.currency === 'USD' ? fmtUsd(debt.amount) : fmtUzs(debt.amount)}
                      </p>
                      <div className="flex items-center gap-1 mt-2 justify-end flex-wrap">
                        {(debt.status === 'active' || debt.status === 'partial' || debt.status === 'overdue') && (
                          <>
                            {/* The borrower of a shared debt reports payments with the "I made a payment" button below
                                (one entry point, so the same money can't also go in as a request) */}
                            {!(debt.mirror_debt_id && debt.direction === 'received') && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openPaymentDialog(debt)}
                                className="h-7 px-2 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                title={t('actions.pay')}
                              >
                                <DollarSign className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            {debt.mirror_debt_id && debt.direction === 'received' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setBorrowerPayDebt(debt)}
                                className="h-7 px-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                title={t('actions.borrowerPay')}
                                data-testid="borrower-pay-btn"
                              >
                                <Wallet className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            {!debt.mirror_debt_id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleSettle(debt)}
                                className="h-7 px-2 text-green-600 hover:text-green-700 hover:bg-green-50"
                                title={t('actions.settle')}
                              >
                                <CheckCircle2 className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </>
                        )}
                        {debt.status === 'settled' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openNewForCounterparty(debt)}
                            className="h-7 px-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                            title={t('actions.newForCounterparty', { name: debt.counterparty_name })}
                          >
                            <Plus className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {debt.paid_amount > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => togglePaymentHistory(debt.id)}
                            className="h-7 px-2 text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                            title={t('actions.paymentHistory')}
                          >
                            <History className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {/* Edit: only for solo debts or rejected debts */}
                        {(!debt.mirror_debt_id || debt.status === 'rejected') && debt.status !== 'settled' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEdit(debt)}
                            className="h-7 px-2"
                            title={t('common:actions.edit')}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {/* Resubmit: rejected debts with a FinOps user or an external (Telegram) counterparty */}
                        {debt.status === 'rejected' && (debt.counterparty_user_id || debt.counterparty_phone) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleResubmit(debt.id)}
                            className="h-7 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            title={t('actions.resubmit')}
                          >
                            <Link2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {/* The borrower's own payment reports on a shared debt */}
                        {debt.mirror_debt_id && debt.direction === 'received' && !['pending_approval', 'rejected'].includes(debt.status) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setExpandedMyClaimsId(expandedMyClaimsId === debt.id ? null : debt.id)}
                            className={`h-7 px-2 gap-1 ${expandedMyClaimsId === debt.id ? 'text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`}
                            title={t('actions.myPayments')}
                          >
                            <Receipt className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {/* Payments reported from the Telegram bot */}
                        {debt.payment_claims > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setExpandedClaimsId(expandedClaimsId === debt.id ? null : debt.id)}
                            className={`h-7 px-2 gap-1 ${expandedClaimsId === debt.id ? 'text-amber-600 bg-amber-50 dark:bg-amber-900/20' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`}
                            title={t('actions.claims')}
                          >
                            <Receipt className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {/* Approval history: FinOps counterparty or external (Telegram) counterparty */}
                        {(debt.counterparty_user_id || debt.counterparty_phone) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleApprovalHistory(debt.id)}
                            className={`h-7 px-2 gap-1 ${expandedHistoryId === debt.id ? 'text-violet-600 bg-violet-50 dark:bg-violet-900/20' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`}
                            title={t('actions.approvalHistory')}
                          >
                            <ScrollText className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {/* Delete: hidden for settled debts and active/partial mirror debts */}
                        {debt.status !== 'settled' && !(debt.mirror_debt_id && ['active', 'partial'].includes(debt.status)) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(debt)}
                            className="h-7 px-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                            title={t('common:actions.delete')}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Rejection reason banner — shown prominently for rejected debts */}
                  {debt.status === 'rejected' && (debt.rejection_reason || debt.counterparty_rejection_reason) && (
                    <div className="mt-2 flex items-start gap-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-xs text-red-700 dark:text-red-400" data-testid="debt-rejection-reason">
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                      <span>
                        <strong>{t('rejection.reasonLabel')}</strong> {debt.rejection_reason || debt.counterparty_rejection_reason}
                        {!debt.counterparty_user_id && debt.counterparty_phone && (
                          <span className="block mt-0.5 text-red-600/80 dark:text-red-400/80">
                            {t('rejection.resubmitHint')}
                          </span>
                        )}
                      </span>
                    </div>
                  )}

                  {expandedClaimsId === debt.id && (
                    <DebtPaymentClaims debt={debt} onChanged={fetchDebts} />
                  )}
                  {expandedMyClaimsId === debt.id && (
                    <BorrowerPaymentClaims debt={debt} refreshKey={myClaimsRefresh} />
                  )}

                  {/* Approval history expandable */}
                  {expandedHistoryId === debt.id && (
                    <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                      <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-1.5 mb-3">
                        <ScrollText className="h-4 w-4" /> {t('history.title')}
                      </h4>
                      {loadingHistory[debt.id] ? (
                        <div className="flex justify-center py-3">
                          <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                        </div>
                      ) : !approvalHistories[debt.id]?.length ? (
                        <p className="text-xs text-slate-400 text-center py-2">{t('history.empty')}</p>
                      ) : (
                        <div className="relative pl-4">
                          {/* Vertical timeline line */}
                          <div className="absolute left-1.5 top-0 bottom-0 w-px bg-slate-200 dark:bg-slate-700" />
                          <div className="space-y-3">
                            {approvalHistories[debt.id].map((ev, idx) => {
                              const viaTelegram = ev.payload?.channel === 'telegram';
                              const typeLabel = viaTelegram
                                ? (ev.payload?.resubmitted ? t('history.type.telegramResubmitted') : t('history.type.telegram'))
                                : ({ creation: t('history.type.creation'), payment: t('history.type.payment'), deletion: t('history.type.deletion') }[ev.type] || ev.type);
                              const statusColor = ev.status === 'approved'
                                ? 'text-green-600 dark:text-green-400'
                                : ev.status === 'rejected'
                                  ? 'text-red-600 dark:text-red-400'
                                  : ev.status === 'cancelled'
                                    ? 'text-slate-500 dark:text-slate-400'
                                    : 'text-yellow-600 dark:text-yellow-400';
                              const statusLabel = { approved: t('history.status.approved'), rejected: t('history.status.rejected'), pending: t('history.status.pending'), cancelled: t('history.status.cancelled') }[ev.status] || ev.status;
                              const dotColor = ev.status === 'approved'
                                ? 'bg-green-500'
                                : ev.status === 'rejected'
                                  ? 'bg-red-500'
                                  : ev.status === 'cancelled'
                                    ? 'bg-slate-400'
                                    : 'bg-yellow-400';
                              const reason = ev.payload?.rejection_reason;
                              const payAmt = ev.payload?.amount
                                ? (ev.payload.currency === 'UZS'
                                  ? `${Math.round(ev.payload.amount).toLocaleString('uz-UZ')} UZS`
                                  : `$${Number(ev.payload.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}`)
                                : null;
                              return (
                                <div key={ev.id || idx} className="relative flex items-start gap-2.5">
                                  <div className={`absolute -left-2.5 mt-1 h-2.5 w-2.5 rounded-full border-2 border-white dark:border-slate-800 ${dotColor}`} />
                                  <div className="ml-2 flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="text-xs font-medium text-slate-700 dark:text-slate-300">{typeLabel}</span>
                                      <span className={`text-xs font-semibold ${statusColor}`}>{statusLabel}</span>
                                      {ev.status === 'pending' && (
                                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">{t('history.status.pending')}</span>
                                      )}
                                    </div>
                                    <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 flex flex-wrap gap-x-2">
                                      <span>{ev.requested_by_name}{viaTelegram && ev.target_name ? ` → ${ev.target_name}` : ''}</span>
                                      {payAmt && <span>• {payAmt}</span>}
                                      {viaTelegram && ev.status === 'pending' && !ev.payload?.delivered && (
                                        <span className="text-amber-600 dark:text-amber-400">• {t('history.notDelivered')}</span>
                                      )}
                                      {ev.resolved_at && ev.status !== 'pending' && (
                                        <span>• {t('history.answered', { date: new Date(ev.resolved_at).toLocaleDateString('uz-UZ', { day: '2-digit', month: 'short', year: 'numeric' }) })}</span>
                                      )}
                                      <span>• {new Date(ev.created_at).toLocaleDateString('uz-UZ', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                                    </div>
                                    {reason && (
                                      <div className="mt-1 text-[11px] text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded px-2 py-1">
                                        {t('history.reason', { reason })}
                                      </div>
                                    )}
                                    {ev.payload?.resubmitted && (
                                      <div className="mt-1 text-[11px] text-blue-600 dark:text-blue-400">
                                        ↩ {t('history.resubmitted')}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Payment history expandable */}
                  {isExpanded && (
                    <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
                          <History className="h-4 w-4" /> {t('payments.title')}
                        </h4>
                        <Button variant="ghost" size="sm" onClick={() => setExpandedDebtId(null)} className="h-6 px-1">
                          <ChevronUp className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                      {isLoadingPayments ? (
                        <div className="flex justify-center py-3">
                          <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                        </div>
                      ) : debtPayments.length === 0 ? (
                        <p className="text-xs text-slate-400 text-center py-2">{t('payments.empty')}</p>
                      ) : (
                        <div className="space-y-1.5">
                          {debtPayments.map(p => (
                            <div key={p.id} className="flex items-center justify-between p-2 rounded-lg bg-slate-50 dark:bg-slate-800/50 text-sm">
                              <div className="flex items-center gap-2">
                                <DollarSign className="h-3.5 w-3.5 text-green-500" />
                                <span className="font-medium text-slate-700 dark:text-slate-300">
                                  {debt.currency === 'USD' ? fmtUsd(p.amount) : fmtUzs(p.amount)}
                                </span>
                                <span className="text-xs text-slate-400">
                                  {new Date(p.payment_date).toLocaleDateString('uz-UZ')}
                                </span>
                                {p.note && <span className="text-xs text-slate-400 max-w-[150px]">— <TruncatedText text={p.note} maxLength={30} className="truncate inline" /></span>}
                              </div>
                              {!debt.mirror_debt_id && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeletePayment(debt.id, p.id)}
                                  className="h-6 px-1.5 text-red-400 hover:text-red-600 hover:bg-red-50"
                                  title={t('payments.delete')}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Payment Dialog */}
      <Dialog open={!!paymentDebt} onOpenChange={(open) => { if (!open) closePaymentDialog(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-amber-500" />
              {t('paymentDialog.title')}
            </DialogTitle>
          </DialogHeader>
          {paymentDebt && (
            <div className="space-y-4">
              {paymentDebt.mirror_debt_id && (
                <div className="flex items-start gap-2 p-2.5 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 text-xs text-yellow-700 dark:text-yellow-400">
                  <Clock className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  <span>{t('paymentDialog.mirrorNotice')}</span>
                </div>
              )}
              <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">{paymentDebt.counterparty_name}</span>
                  <span className="font-medium">
                    {paymentDebt.currency === 'USD' ? fmtUsd(paymentDebt.amount) : fmtUzs(paymentDebt.amount)}
                  </span>
                </div>
                {paymentDebt.paid_amount > 0 && (
                  <div className="mt-2">
                    <div className="flex justify-between text-xs text-slate-400 mb-1">
                      <span>{t('list.paid', { amount: paymentDebt.currency === 'USD' ? fmtUsd(paymentDebt.paid_amount) : fmtUzs(paymentDebt.paid_amount) })}</span>
                      <span>{t('list.remaining', { amount: paymentDebt.currency === 'USD' ? fmtUsd(paymentDebt.remaining_amount) : fmtUzs(paymentDebt.remaining_amount) })}</span>
                    </div>
                    <div className="w-full h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-amber-500"
                        style={{ width: `${Math.min(100, (paymentDebt.paid_amount / paymentDebt.amount) * 100)}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>{t('paymentDialog.amount', { currency: paymentDebt.currency })}</Label>
                <AmountInput
                  placeholder="0.00"
                  value={paymentAmount}
                  onChange={(val) => setPaymentAmount(val)}
                  autoFocus
                />
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs"
                    onClick={() => setPaymentAmount(String(paymentDebt.remaining_amount))}
                  >
                    {t('paymentDialog.full', { amount: paymentDebt.currency === 'USD' ? fmtUsd(paymentDebt.remaining_amount) : fmtUzs(paymentDebt.remaining_amount) })}
                  </Button>
                  {paymentDebt.remaining_amount > 1 && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs"
                      onClick={() => setPaymentAmount(String(Math.round(paymentDebt.remaining_amount / 2 * 100) / 100))}
                    >
                      {t('paymentDialog.half', { amount: paymentDebt.currency === 'USD' ? fmtUsd(paymentDebt.remaining_amount / 2) : fmtUzs(paymentDebt.remaining_amount / 2) })}
                    </Button>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t('paymentDialog.date')}</Label>
                <Input
                  type="date"
                  value={paymentDate}
                  onChange={e => setPaymentDate(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label>{t('paymentDialog.note')}</Label>
                <Input
                  placeholder={t('paymentDialog.notePlaceholder')}
                  value={paymentNote}
                  onChange={e => setPaymentNote(e.target.value)}
                />
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={closePaymentDialog}>{t('common:actions.cancel')}</Button>
                <Button onClick={handlePayment} disabled={payingDebt}>
                  {payingDebt ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <DollarSign className="h-4 w-4 mr-2" />}
                  {t('paymentDialog.submit')}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Rejection reason dialog */}
      <Dialog open={!!rejectingApproval} onOpenChange={(open) => { if (!open) { setRejectingApproval(null); setRejectReason(''); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <X className="h-5 w-5" /> {t('rejectDialog.title')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {t('rejectDialog.hint')}
            </p>
            <Input
              placeholder={t('rejectDialog.placeholder')}
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleResolveApproval(rejectingApproval, 'reject', rejectReason); }}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejectingApproval(null); setRejectReason(''); }}>{t('common:actions.cancel')}</Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={resolvingApproval === rejectingApproval}
              onClick={() => handleResolveApproval(rejectingApproval, 'reject', rejectReason)}
            >
              {resolvingApproval === rejectingApproval ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              {t('rejectDialog.submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {borrowerPayDebt && (
        <BorrowerPaymentDialog
          debt={borrowerPayDebt}
          open={!!borrowerPayDebt}
          onClose={closeBorrowerPay}
          onDone={() => { fetchDebts(); setMyClaimsRefresh((n) => n + 1); setExpandedMyClaimsId(borrowerPayDebt.id); }}
        />
      )}
    </div>
  );
}

export default Debts;
